import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Plus,
  MoreHorizontal,
  Mail,
  UserRound,
  Users,
  BriefcaseBusiness,
  Building2,
  LayoutGrid,
  List,
  ChevronRight
} from 'lucide-react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import ConfirmModal from '../ConfirmModal'
import { useUser } from '../UserContext'
import { withOrg } from '../lib/withOrg'

const EMPTY_FORM = {
  name: '',
  industry: '',
  primary_contact_name: '',
  primary_contact_email: '',
  notes: '',
  assigned_to: '',
  business_unit_ids: []
}

const REQUIRED_FIELDS = [
  { key: 'name', label: 'Client name' },
  { key: 'industry', label: 'Industry' },
  { key: 'primary_contact_name', label: 'Contact name' },
  { key: 'primary_contact_email', label: 'Contact email' },
]

function requiredLabel(label) {
  return (
    <>
      {label} <span style={{ color: 'var(--hv-red)' }}>*</span>
    </>
  )
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function normaliseName(value) {
  return value.trim().toLowerCase().replace(/\s+/g, ' ')
}

function getInitials(name) {
  if (!name) return '?'
  return name
    .split(' ')
    .map(part => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export default function Clients() {
  const navigate = useNavigate()
  const toast = useToast()
  const { orgId, user, isAdmin, isPlatformAdmin, can, businessUnits, activeBusinessUnitId } = useUser()
  const canManageClients = can('clients.manage')

  const [clients, setClients] = useState([])
  const [deals, setDeals] = useState([])
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [search, setSearch] = useState('')
  const [industryFilter, setIndustryFilter] = useState('')
  const [assignedFilter, setAssignedFilter] = useState('')
  const [sortBy, setSortBy] = useState('name_asc')
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('delivr-clients-view') || '')
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [openMenu, setOpenMenu] = useState(null)

  useEffect(() => {
    fetchClients()
    fetchProfiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessUnitId, orgId, user?.id])

  function validateClient(payload) {
    for (const field of REQUIRED_FIELDS) {
      if (!payload[field.key]?.trim()) {
        toast.error(`${field.label} is required`)
        return false
      }
    }

    if (!isValidEmail(payload.primary_contact_email)) {
      toast.error('Please enter a valid contact email')
      return false
    }

    return true
  }

  async function fetchClients() {
    setError(null)
    setLoading(true)

    if (!user) {
      setLoading(false)
      return
    }

    const [
      { data: profileData },
      { data: dealData },
      { data: businessLinkData, error: businessLinkError },
      { data: dealBusinessLinkData, error: dealBusinessLinkError },
    ] = await Promise.all([
      supabase
        .from('profiles')
        .select('id, email, full_name'),

      supabase
        .from('secure_deals')
        .select('id, client_id, name, value, currency, status, end_date'),

      supabase
        .from('client_business_units')
        .select('client_id, business_unit_id'),

      supabase
        .from('deal_business_units')
        .select('deal_id, business_unit_id')
    ])

    let query = supabase
      .from('clients')
      .select('*')
      .order('created_at', { ascending: false })

    const { data: clientData, error: fetchError } = await query

    if (fetchError) {
      console.error('Failed to load clients:', fetchError)
      setError(`Failed to load clients: ${fetchError.message}`)
      setLoading(false)
      return
    }
    if (businessLinkError || dealBusinessLinkError) {
      const linkError = businessLinkError || dealBusinessLinkError
      console.error('Failed to load Business assignments:', linkError)
      setError(`Failed to load Business assignments: ${linkError.message}`)
      setLoading(false)
      return
    }

    const profileMap = {}

    profileData?.forEach(p => {
      profileMap[p.id] = p.full_name || p.email
    })

    const businessIdsByClient = (businessLinkData || []).reduce((map, link) => {
      if (!map[link.client_id]) map[link.client_id] = []
      map[link.client_id].push(link.business_unit_id)
      return map
    }, {})

    setClients((clientData || []).map(c => ({
      ...c,
      business_unit_ids: businessIdsByClient[c.id] || [],
      assignedEmail: c.assigned_to ? profileMap[c.assigned_to] || '—' : '—'
    })).filter(client => !activeBusinessUnitId || client.business_unit_ids.includes(activeBusinessUnitId)))

    const activeDealIds = new Set((dealBusinessLinkData || [])
      .filter(link => !activeBusinessUnitId || link.business_unit_id === activeBusinessUnitId)
      .map(link => link.deal_id))
    setDeals((dealData || []).filter(deal => !activeBusinessUnitId || activeDealIds.has(deal.id)))
    setLoading(false)
  }

  async function fetchProfiles() {
    const { data } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .order('full_name')

    setProfiles(data || [])
  }

  async function addClient() {
    if (!canManageClients) return toast.error('You do not have permission to add clients')
    if (!validateClient(form)) return
    if (clients.some(client => normaliseName(client.name) === normaliseName(form.name))) {
      return toast.error('A client with this name already exists')
    }

    setSaving(true)

    const payload = {
      ...form,
      name: form.name.trim(),
      industry: form.industry.trim(),
      primary_contact_name: form.primary_contact_name.trim(),
      primary_contact_email: form.primary_contact_email.trim(),
      notes: form.notes || null,
      assigned_to: form.assigned_to || (!isAdmin && !isPlatformAdmin ? user?.id : null),
    }

    const businessUnitIds = form.business_unit_ids.length
      ? form.business_unit_ids
      : activeBusinessUnitId ? [activeBusinessUnitId] : []

    if (!businessUnitIds.length) {
      setSaving(false)
      return toast.error('Select at least one Business')
    }

    delete payload.business_unit_ids
    const { data: createdClient, error } = await supabase
      .from('clients')
      .insert([withOrg(payload, orgId)])
      .select('id')
      .single()

    if (error) {
      setSaving(false)
      console.error('Failed to add client:', error)
      toast.error(error.code === '23505' ? error.message : `Failed to add client: ${error.message}`)
      return
    }

    const { error: businessError } = await supabase.rpc('replace_client_business_units', {
      target_client_id: createdClient.id,
      target_business_unit_ids: businessUnitIds
    })
    if (businessError) {
      await supabase.from('clients').delete().eq('id', createdClient.id)
      setSaving(false)
      return toast.error(`Failed to assign Business access: ${businessError.message}`)
    }

    setSaving(false)
    toast.success('Client added successfully')
    setForm(EMPTY_FORM)
    setShowForm(false)
    fetchClients()
  }

  async function deleteClient(id) {
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Failed to delete client:', error)
      toast.error(`Failed to delete client: ${error.message}`)
    } else {
      toast.success('Client deleted')
    }

    setConfirmDelete(null)
    setOpenMenu(null)
    fetchClients()
  }

  function openEdit(client) {
    setEditingId(client.id)
    setOpenMenu(null)
    setEditForm({
      name: client.name || '',
      industry: client.industry || '',
      primary_contact_name: client.primary_contact_name || '',
      primary_contact_email: client.primary_contact_email || '',
      notes: client.notes || '',
      assigned_to: client.assigned_to || '',
      business_unit_ids: client.business_unit_ids || []
    })

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function saveEdit(id) {
    if (!validateClient(editForm)) return
    if (clients.some(client => client.id !== id && normaliseName(client.name) === normaliseName(editForm.name))) {
      return toast.error('A client with this name already exists')
    }

    setSaving(true)

    const payload = {
      ...editForm,
      name: editForm.name.trim(),
      industry: editForm.industry.trim(),
      primary_contact_name: editForm.primary_contact_name.trim(),
      primary_contact_email: editForm.primary_contact_email.trim(),
      notes: editForm.notes || null,
      assigned_to: editForm.assigned_to || null,
    }

    const businessUnitIds = editForm.business_unit_ids || []
    if (!businessUnitIds.length) {
      setSaving(false)
      return toast.error('Select at least one Business')
    }
    delete payload.business_unit_ids
    const { error } = await supabase
      .from('clients')
      .update(payload)
      .eq('id', id)

    if (error) {
      setSaving(false)
      console.error('Failed to save client:', error)
      toast.error(error.code === '23505' ? error.message : `Failed to save changes: ${error.message}`)
      return
    }

    const { error: businessError } = await supabase.rpc('replace_client_business_units', {
      target_client_id: id,
      target_business_unit_ids: businessUnitIds
    })
    if (businessError) {
      setSaving(false)
      return toast.error(`Client saved, but Business access failed: ${businessError.message}`)
    }

    setSaving(false)
    toast.success('Client updated')
    setEditingId(null)
    fetchClients()
  }

  const enrichedClients = useMemo(() => {
    return clients.map(client => {
      const clientDeals = deals.filter(d => d.client_id === client.id)
      const activeDeals = clientDeals.filter(d => d.status === 'active')
return {
  ...client,
  deals: clientDeals,
  activeDeals,
}
    })
  }, [clients, deals])

  const industries = [
    ...new Set(
      clients
        .map(c => c.industry)
        .filter(Boolean)
    )
  ].sort()

  const filtered = [...enrichedClients]
    .filter(client => {
      const matchesSearch =
        (client.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (client.industry || '').toLowerCase().includes(search.toLowerCase()) ||
        (client.primary_contact_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (client.primary_contact_email || '').toLowerCase().includes(search.toLowerCase()) ||
        (client.assignedEmail || '').toLowerCase().includes(search.toLowerCase())

      const matchesIndustry = industryFilter ? client.industry === industryFilter : true
      const matchesAssigned = assignedFilter ? client.assigned_to === assignedFilter : true

      return matchesSearch && matchesIndustry && matchesAssigned
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '')

        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '')

        case 'industry_asc':
          return (a.industry || '').localeCompare(b.industry || '')

        case 'industry_desc':
          return (b.industry || '').localeCompare(a.industry || '')

        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0)

        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0)

        default:
          return (a.name || '').localeCompare(b.name || '')
      }
    })

  const activeDealsCount = enrichedClients.reduce((sum, c) => sum + c.activeDeals.length, 0)
  const clientToDelete = confirmDelete ? clients.find(c => c.id === confirmDelete) : null
  const clientBeingEdited = editingId ? clients.find(c => c.id === editingId) : null
  const effectiveViewMode = viewMode || (enrichedClients.length > 6 ? 'list' : 'cards')

  function changeViewMode(mode) {
    setViewMode(mode)
    localStorage.setItem('delivr-clients-view', mode)
    setOpenMenu(null)
  }

  return (
    <div className="hv-page">
      <style>{`
        .clients-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 22px;
        }

        .clients-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .clients-search {
          position: relative;
          width: 300px;
        }

        .clients-summary {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 18px;
        }

        .clients-summary-item {
          display: flex;
          min-width: 0;
          align-items: center;
          gap: 15px;
          padding: 17px 18px;
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 8px;
          box-shadow: var(--hv-shadow-sm);
        }

        .clients-summary-icon { display: inline-flex; width: 44px; height: 44px; flex: 0 0 44px; align-items: center; justify-content: center; border-radius: 50%; }
        .clients-summary-icon.indigo { color: #4F46E5; background: #EEF2FF; }
        .clients-summary-icon.green { color: #059669; background: #E8F8F2; }
        .clients-summary-icon.blue { color: #2563EB; background: #EFF6FF; }
        .clients-summary-copy { min-width: 0; flex: 1; }
        .clients-summary-arrow { flex: 0 0 auto; color: #91A0BC; }

        .clients-summary-value {
          font-size: 21px;
          font-weight: 800;
          color: var(--hv-text-primary);
          letter-spacing: -0.6px;
          line-height: 1;
          margin-bottom: 5px;
        }

        .clients-summary-label {
          font-size: 11px;
          font-weight: 700;
          color: var(--hv-text-muted);
        }

        .clients-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
        }

        .clients-view-toggle { display: inline-flex; flex: 0 0 auto; padding: 3px; border: 1px solid var(--hv-border); border-radius: 8px; background: #fff; }
        .clients-view-toggle button { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 28px; padding: 0; border: 0; border-radius: 5px; background: transparent; color: var(--hv-text-muted); cursor: pointer; }
        .clients-view-toggle button.active { background: var(--hv-accent-light); color: var(--hv-accent); }

        .clients-list-wrap { overflow: visible; }
        .clients-viewbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin: 24px 0 10px; }
        .clients-viewbar h2 { display: inline-flex; align-items: center; gap: 9px; margin: 0; font-size: 16px; }
        .clients-total-pill { padding: 4px 8px; border-radius: 999px; background: #F0F3F9; color: var(--hv-text-muted); font-size: 10px; font-weight: 700; }
        .clients-list { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .clients-list th { position: sticky; top: 0; z-index: 2; padding: 11px 14px; border-bottom: 1px solid var(--hv-border); background: #F8FAFD; color: var(--hv-text-muted); font-size: 10px; font-weight: 800; text-align: left; text-transform: uppercase; }
        .clients-list td { padding: 12px 14px; border-bottom: 1px solid var(--hv-border-light); color: var(--hv-text-secondary); font-size: 12px; vertical-align: middle; }
        .clients-list tbody tr { background: #fff; cursor: pointer; transition: background 0.12s ease; }
        .clients-list tbody tr:hover { background: var(--hv-surface-hover); }
        .clients-list tbody tr:last-child td { border-bottom: 0; }
        .clients-list-identity { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .clients-list-logo { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 32px; width: 32px; height: 32px; overflow: hidden; border-radius: 6px; background: var(--hv-accent-light); color: var(--hv-accent); font-size: 10px; font-weight: 800; }
        .clients-list-logo img { width: 100%; height: 100%; object-fit: contain; }
        .clients-list-name, .clients-list-subtext { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .clients-list-name { color: var(--hv-text-primary); font-weight: 800; }
        .clients-list-subtext { margin-top: 3px; color: var(--hv-text-muted); font-size: 10px; }
        .clients-list-number { color: var(--hv-text-primary); font-weight: 800; }
        .client-list-actions { position: relative; text-align: right; }
        .client-list-menu-trigger { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; padding: 0; border: 0; border-radius: 6px; background: transparent; color: var(--hv-text-muted); cursor: pointer; }
        .client-list-menu-trigger:hover, .client-list-menu-trigger:focus-visible { background: var(--hv-accent-light); color: var(--hv-accent); outline: none; }
        .client-list-actions .client-menu { top: 36px; right: 8px; text-align: left; }

        .client-card {
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(15,21,35,0.05);
          overflow: hidden;
          cursor: pointer;
          position: relative;
          transition: transform 0.14s ease, box-shadow 0.14s ease, border-color 0.14s ease;
        }

        .client-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 35px rgba(15,21,35,0.08);
          border-color: #D8DEEB;
        }

        .client-card-body {
          padding: 20px;
        }

        .client-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
        }

        .client-card-main {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .client-logo {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background: linear-gradient(135deg, #EEF2FF, #F5F3FF);
          color: #4F46E5;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 13px;
          flex-shrink: 0;
        }

        .client-name {
          font-size: 15px;
          font-weight: 800;
          color: var(--hv-text-primary);
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .client-industry {
          display: inline-flex;
          margin-top: 6px;
          font-size: 11px;
          font-weight: 700;
          color: #5B21B6;
          background: #F1EDFF;
          border-radius: 999px;
          padding: 3px 8px;
        }

        .client-card-stats {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
          margin: 18px 0;
        }

        .client-stat {
          background: #FAFBFF;
          border: 1px solid var(--hv-border-light);
          border-radius: 11px;
          padding: 12px;
        }

        .client-stat-value {
          font-size: 18px;
          font-weight: 800;
          color: var(--hv-text-primary);
          letter-spacing: -0.4px;
          margin-bottom: 3px;
        }

        .client-stat-label {
          font-size: 11px;
          color: var(--hv-text-muted);
          font-weight: 700;
        }

        .client-contact-box {
          border-top: 1px solid var(--hv-border-light);
          padding-top: 15px;
          display: grid;
          gap: 9px;
          font-size: 12px;
        }

        .client-contact-row {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--hv-text-secondary);
          min-width: 0;
        }

        .client-contact-row span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .client-card-footer {
          border-top: 1px solid var(--hv-border-light);
          padding: 12px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: var(--hv-text-muted);
          font-size: 11px;
        }

        .client-menu {
          position: absolute;
          top: 46px;
          right: 16px;
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 12px;
          box-shadow: 0 14px 40px rgba(15,21,35,0.16);
          padding: 6px;
          z-index: 20;
          min-width: 140px;
        }

        @media (max-width: 1400px) {
          .clients-topbar { align-items: stretch; flex-direction: column; }
          .clients-actions { flex-wrap: wrap; }
          .clients-search { flex: 1 1 280px; }
        }

        @media (max-width: 1100px) {
          .clients-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .clients-summary {
            grid-template-columns: repeat(2, 1fr);
            row-gap: 18px;
          }

          .clients-summary-item {
            padding: 17px 18px;
          }
        }

        @media (max-width: 700px) {
          .clients-actions { align-items: stretch; flex-direction: column; }
          .clients-search, .clients-actions .hv-select, .clients-actions .hv-btn { width: 100% !important; }
          .clients-summary { grid-template-columns: 1fr; }
          .clients-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="clients-topbar">
        <div>
          <h1 className="hv-page-title">Clients</h1>
          <p className="hv-page-subtitle">Manage client relationships, track deals and keep everything in one place.</p>
        </div>

        <div className="clients-actions">
          <div className="clients-search">
            <span className="hv-search-icon">
              <Search size={14} />
            </span>

            <input
              className="hv-search"
              type="text"
              placeholder="Search clients..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <select className="hv-select" style={{ width: 150, height: 36 }} value={industryFilter} onChange={e => setIndustryFilter(e.target.value)}>
            <option value="">All Industries</option>
            {industries.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>

          <select className="hv-select" style={{ width: 150, height: 36 }} value={assignedFilter} onChange={e => setAssignedFilter(e.target.value)}>
            <option value="">All Owners</option>
            {profiles.map(p => (
              <option key={p.id} value={p.id}>{p.full_name || p.email}</option>
            ))}
          </select>

          <select className="hv-select" style={{ width: 165, height: 36 }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="name_asc">Client A → Z</option>
            <option value="name_desc">Client Z → A</option>
            <option value="industry_asc">Industry A → Z</option>
            <option value="industry_desc">Industry Z → A</option>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>

          {canManageClients && <button
            className="hv-btn hv-btn-primary"
            onClick={() => {
              setForm({ ...EMPTY_FORM, business_unit_ids: activeBusinessUnitId ? [activeBusinessUnitId] : [] })
              setShowForm(!showForm)
              setEditingId(null)
            }}
          >
            {showForm ? 'Cancel' : (
              <>
                <Plus size={15} />
                Add Client
              </>
            )}
          </button>}
        </div>
      </div>

      {(showForm || editingId) && (
        <ClientForm
          title={editingId ? `Edit ${clientBeingEdited?.name || 'Client'}` : 'New Client'}
          form={editingId ? editForm : form}
          setForm={editingId ? setEditForm : setForm}
          profiles={profiles}
          businessUnits={businessUnits}
          saving={saving}
          onCancel={() => {
            setShowForm(false)
            setEditingId(null)
          }}
          onSave={() => editingId ? saveEdit(editingId) : addClient()}
          saveLabel={editingId ? 'Save Changes' : 'Save Client'}
        />
      )}

      <div className="clients-summary">
        <SummaryItem value={enrichedClients.length} label="Total Clients" icon={<Users size={21} />} tone="indigo" />
        <SummaryItem value={activeDealsCount} label="Active Deals" icon={<BriefcaseBusiness size={21} />} tone="green" />
        <SummaryItem value={industries.length} label="Industries Covered" icon={<Building2 size={21} />} tone="blue" />
      </div>

      <div className="clients-viewbar"><h2>Clients <span className="clients-total-pill">{filtered.length} total</span></h2><div className="clients-view-toggle" aria-label="Client view"><button className={effectiveViewMode === 'list' ? 'active' : ''} onClick={() => changeViewMode('list')} title="List view" aria-label="List view"><List size={16} /></button><button className={effectiveViewMode === 'cards' ? 'active' : ''} onClick={() => changeViewMode('cards')} title="Card view" aria-label="Card view"><LayoutGrid size={16} /></button></div></div>

      {error && (
        <div style={{ background: 'var(--hv-red-bg)', border: '1px solid #fca5a5', borderRadius: 'var(--hv-radius)', padding: '14px 18px', color: 'var(--hv-red-text)', fontSize: '13.5px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--hv-text-muted)', fontSize: '13.5px', padding: '40px 0' }}>
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="hv-table-wrap">
          <div className="hv-empty">
            <div className="hv-empty-title">{search ? 'No clients match your filters' : 'No clients yet'}</div>
            <div className="hv-empty-text">{search ? 'Try adjusting your search.' : 'Add your first client using the button above.'}</div>
          </div>
        </div>
      ) : effectiveViewMode === 'list' ? (
        <div className="hv-table-wrap clients-list-wrap">
          <table className="clients-list">
            <thead><tr><th style={{ width: '27%' }}>Client</th><th style={{ width: '15%' }}>Industry</th><th style={{ width: '11%' }}>Deals</th><th style={{ width: '23%' }}>Primary contact</th><th style={{ width: '19%' }}>Owner</th><th style={{ width: 48 }}></th></tr></thead>
            <tbody>{filtered.map(client => <ClientListRow key={client.id} client={client} openMenu={openMenu} setOpenMenu={setOpenMenu} onOpen={() => navigate(`/clients/${client.id}`)} onEdit={() => openEdit(client)} onDelete={() => setConfirmDelete(client.id)} />)}</tbody>
          </table>
        </div>
      ) : (
        <div className="clients-grid">
          {filtered.map(client => (
            <ClientCard
              key={client.id}
              client={client}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              onOpen={() => navigate(`/clients/${client.id}`)}
              onEdit={() => openEdit(client)}
              onDelete={() => setConfirmDelete(client.id)}
            />
          ))}
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete client"
          message={`"${clientToDelete?.name}" will be permanently removed. This cannot be undone.`}
          confirmLabel="Delete Client"
          onConfirm={() => deleteClient(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}

function ClientForm({ title, form, setForm, profiles, businessUnits, saving, onCancel, onSave, saveLabel }) {
  return (
    <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--hv-text-primary)', marginBottom: '6px' }}>
            {title}
          </div>
          <p style={{ fontSize: '13px', color: 'var(--hv-text-muted)' }}>
            Capture the key account details, contact owner and relationship notes.
          </p>
        </div>

        <button className="hv-btn hv-btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label className="hv-form-label">{requiredLabel('Client Name')}</label>
          <input className="hv-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>

        <div>
          <label className="hv-form-label">{requiredLabel('Industry')}</label>
          <input className="hv-input" value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} />
        </div>

        <div>
          <label className="hv-form-label">{requiredLabel('Contact Name')}</label>
          <input className="hv-input" value={form.primary_contact_name} onChange={e => setForm({ ...form, primary_contact_name: e.target.value })} />
        </div>

        <div>
          <label className="hv-form-label">{requiredLabel('Contact Email')}</label>
          <input className="hv-input" type="email" value={form.primary_contact_email} onChange={e => setForm({ ...form, primary_contact_email: e.target.value })} />
        </div>

        <div>
          <label className="hv-form-label">Notes</label>
          <input className="hv-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
        </div>

        <div>
          <label className="hv-form-label">Assign To</label>
          <select className="hv-select" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
            <option value="">Unassigned</option>
            {profiles.map(p => (
              <option key={p.id} value={p.id}>{p.full_name || p.email}</option>
            ))}
          </select>
        </div>

        {businessUnits.length > 1 && <div style={{ gridColumn: '1 / -1' }}>
          <label className="hv-form-label">Businesses <span style={{ color: 'var(--hv-red)' }}>*</span></label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {businessUnits.map(unit => {
              const selected = (form.business_unit_ids || []).includes(unit.id)
              return <label key={unit.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 11px', border: '1px solid var(--hv-border)', borderRadius: 7, cursor: 'pointer' }}>
                <input type="checkbox" checked={selected} onChange={() => setForm({
                  ...form,
                  business_unit_ids: selected
                    ? form.business_unit_ids.filter(id => id !== unit.id)
                    : [...(form.business_unit_ids || []), unit.id]
                })} />
                <span>{unit.name}</span>
              </label>
            })}
          </div>
        </div>}
      </div>

      <button className="hv-btn hv-btn-primary" onClick={onSave} disabled={saving}>
        {saving ? 'Saving...' : saveLabel}
      </button>
    </div>
  )
}

function SummaryItem({ value, label, icon, tone }) {
  return (
    <div className="clients-summary-item">
      <div className={`clients-summary-icon ${tone}`}>{icon}</div>
      <div className="clients-summary-copy"><div className="clients-summary-value">{value}</div><div className="clients-summary-label">{label}</div></div>
      <ChevronRight className="clients-summary-arrow" size={16} />
    </div>
  )
}

function ClientCard({ client, openMenu, setOpenMenu, onOpen, onEdit, onDelete }) {
  return (
    <div className="client-card" onClick={onOpen}>
      <div className="client-card-body">
        <div className="client-card-header">
          <div className="client-card-main">
            <div className="client-logo">
  {client.logo_url ? (
    <img
      src={client.logo_url}
      alt={client.name}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        borderRadius: '12px'
      }}
    />
  ) : (
    getInitials(client.name)
  )}
</div>

            <div style={{ minWidth: 0 }}>
              <div className="client-name">{client.name}</div>
              <div className="client-industry">{client.industry || 'No industry'}</div>
            </div>
          </div>

          <button
            className="hv-btn hv-btn-ghost"
            style={{ padding: 4, color: 'var(--hv-text-muted)' }}
            onClick={e => {
              e.stopPropagation()
              setOpenMenu(openMenu === client.id ? null : client.id)
            }}
          >
            <MoreHorizontal size={17} />
          </button>

          {openMenu === client.id && (
            <div className="client-menu" onClick={e => e.stopPropagation()}>
              <button className="hv-btn hv-btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onOpen}>
                View
              </button>
              <button className="hv-btn hv-btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onEdit}>
                Edit
              </button>
              <button className="hv-btn hv-btn-danger" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onDelete}>
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="client-card-stats">
          <div className="client-stat">
            <div className="client-stat-value">{client.deals.length}</div>
            <div className="client-stat-label">Deals</div>
          </div>

        </div>

        <div className="client-contact-box">
          <div className="client-contact-row">
            <UserRound size={14} />
            <span>{client.primary_contact_name || 'No primary contact'}</span>
          </div>

          <div className="client-contact-row">
            <Mail size={14} />
            <span>{client.primary_contact_email || 'No email'}</span>
          </div>

          <div className="client-contact-row">
            <BriefcaseBusiness size={14} />
            <span>{client.assignedEmail || 'Unassigned'}</span>
          </div>
        </div>
      </div>

<div className="client-card-footer">
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <Building2 size={13} />
    Client Workspace
  </span>

  <span>
    View client →
  </span>
</div>
    </div>
  )
}

function ClientListRow({ client, openMenu, setOpenMenu, onOpen, onEdit, onDelete }) {
  return <tr onClick={onOpen}>
    <td><div className="clients-list-identity"><span className="clients-list-logo">{client.logo_url ? <img src={client.logo_url} alt="" /> : getInitials(client.name)}</span><div style={{ minWidth: 0 }}><span className="clients-list-name">{client.name}</span><span className="clients-list-subtext">{client.primary_contact_email || 'No email'}</span></div></div></td>
    <td>{client.industry || '—'}</td>
    <td><span className="clients-list-number">{client.deals.length}</span><span className="clients-list-subtext">{client.activeDeals.length} active</span></td>
    <td><span className="clients-list-name">{client.primary_contact_name || 'No primary contact'}</span><span className="clients-list-subtext">{client.primary_contact_email || '—'}</span></td>
    <td>{client.assignedEmail || 'Unassigned'}</td>
    <td className="client-list-actions" onClick={event => event.stopPropagation()}><button className="client-list-menu-trigger" aria-label={`Actions for ${client.name}`} title="Client actions" onClick={() => setOpenMenu(openMenu === client.id ? null : client.id)}><MoreHorizontal size={17} /></button>{openMenu === client.id && <div className="client-menu"><button className="hv-btn hv-btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onOpen}>View</button><button className="hv-btn hv-btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onEdit}>Edit</button><button className="hv-btn hv-btn-danger" style={{ width: '100%', justifyContent: 'flex-start', fontSize: '13px' }} onClick={onDelete}>Delete</button></div>}</td>
  </tr>
}
